import React, {
  createContext,
  useCallback,
  useContext,
  useEffect
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';
import { useAppContext } from 'utils/hooks';

const analytics = new AnalyticsBrowser();
const API_KEY = process.env.REACT_APP_SEGMENT_API_KEY;

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 *   page: (pageName: string, pageProperties?: Record<string, any>) => void;
 * }>}
 */
export const SegmentContext = createContext();

export function SegmentProvider({ children = null }) {
  const { isSSREnv } = useAppContext();
  const { userData, storeProperties } = useContext(AuthContext);

  const storeName = _get(storeProperties, 'store_name', '');
  const ssSellerId = _get(storeProperties, 'ss_seller_id', '');
  const storeUniqueName = _get(storeProperties, 'store_unique_name', '');
  const brand = isSSREnv ? 'ShipStation Returns' : 'Return Rabbit';
  const userRole = isSSREnv ? 'SS-ADMIN' : 'RR-ADMIN';
  const merchantId = !!storeUniqueName
    ? `${isSSREnv ? 'SSR' : 'RR'}-${storeUniqueName}`
    : null;

  useEffect(() => {
    const userId = _get(userData, 'userId');
    const email = _get(userData, 'email');
    const firstName = _get(userData, 'firstName');
    const lastName = _get(userData, 'lastName');

    if (API_KEY && userId && merchantId && storeName) {
      try {
        analytics.load({ writeKey: API_KEY });
        analytics.identify(userId, {
          group_id: merchantId,
          cx_merchant_id: merchantId,
          customer_id: userId,
          user_id: userId,
          brand,
          email,
          first_name: firstName,
          last_name: lastName,
          company_name: storeName,
          seller_id: ssSellerId,
          user_role: userRole
        });
      } catch (error) {
        console.error('Failed to identify user in Segment:', error);
      }

      return () => {
        try {
          analytics.reset();
        } catch (error) {
          console.error('Failed to reset Segment:', error);
        }
      };
    }
  }, [brand, userRole, userData, merchantId, storeName, ssSellerId]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      const userId = _get(userData, 'userId', '');
      const email = _get(userData, 'email', '');

      if (!API_KEY || !userId || !merchantId) return;

      try {
        analytics.track(eventName, {
          brand,
          cx_merchant_id: merchantId,
          seller_id: ssSellerId,
          user_id: userId,
          email,
          ...eventProperties
        });
      } catch (error) {
        console.error('Failed to track event in Segment:', error);
      }
    },
    [brand, userData, merchantId, ssSellerId]
  );

  const page = useCallback(
    (pageName = '', pageProperties = {}) => {
      const userId = _get(userData, 'userId', '');
      const email = _get(userData, 'email', '');

      if (!API_KEY || !userId || !merchantId) return;

      try {
        analytics.page(pageName, {
          brand,
          cx_merchant_id: merchantId,
          seller_id: ssSellerId,
          user_id: userId,
          email,
          ...pageProperties
        });
      } catch (error) {
        console.error('Failed to track page view in Segment:', error);
      }
    },
    [brand, userData, merchantId, ssSellerId]
  );

  return (
    <SegmentContext.Provider value={{ track, page }}>
      {children}
    </SegmentContext.Provider>
  );
}
